import * as THREE from 'three';

/** @import { UniformGroup } from "../utils/UniformGroup.js" */

/**
 * Composed of
 *   - 'uniform key' name of a property on the material referencing the THREE.Texture
 *   -  part of the 'GPUBindGroupLayoutEntry' for the texture
 *   -  part of 'GPUTextureBindingLayout' for the texture
 *   -  part of 'GPUBindGroupLayoutEntry' for the sampler
 * @typedef {Object} TextureBindingInfo
 * @property {string} uniformKey name of a property on the material referencing the THREE.Texture
 * @property {GPUIndex32} binding Bind group entry identifier
 * @property {GPUShaderStageFlags} visibility Shader stage visibility flags for the texture.
 * @property {GPUTextureSampleType} sampleType Type required for texture views.
 * @property {GPUIndex32} samplerBinding The samplers bind group entry identifier.
 * @property {GPUShaderStageFlags} samplerVisibility Shader stage visibility flags for the sampler.
 */

export class ShaderMaterialWebGPU {

  // Used by the renderer to quickly identify whether a Material requires custom treatment. (faster than instanceof)
  needsCustomPipeline = true;

  /**
   * General Material properties.
   * Might be moved to a base class later.
   */

  /** @type {number}
   * @note Increments THREE.MaterialIdCount to stay consistent with three materials.
  */
  #id = THREE.MaterialIdCount++;
  get id() {return this.#id;}

  /** @type {string} */
  label = '';
  /**
   * @type {boolean}
   * @note 'depthWriteEnabled' in WebGPU
  */
  depthWrite = true;
  /** @type {boolean} If false overrides depthFunc to 'always' */
  depthTest = true;
  /**
   * @type {string}
   * @note 'depthCompare' in WebGPU
   * @see https://developer.mozilla.org/en-US/docs/Web/API/GPUDevice/createRenderPipeline#depthcompare
  */
  depthFunc = 'less-equal';
  /**
   * @type {THREE.Side} Defines which side of faces is rendered.
   * @note Translates to WebGPU 'cullMode'.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/GPUDevice/createRenderPipeline#cullmode
  */
  side = THREE.DoubleSide;
  /**
   * @type {THREE.Blending}
   * @note Not supported yet but needed for material hash.
  */
  blending = THREE.NormalBlending;
  /**
   * @type {boolean} Whether the material is transparent.
   * @note At time of writing this is not used by the renderer, but can be used by BVH builds.
  */
  transparent = false;

  /** @type {boolean} Enables depth bias (WebGPU depthBias / depthBiasSlopeScale). */
  polygonOffset = false;
  /** @type {number} Maps to WebGPU depthBiasSlopeScale (negated for reversed-Z). */
  polygonOffsetFactor = 0;
  /** @type {number} Maps to WebGPU depthBias (negated for reversed-Z). */
  polygonOffsetUnits = 0;

  /** @type {boolean} Set to trigger a re-upload of uniform values.*/
  uniformsNeedUpdate = true;

  /** @type {Map<GPUIndex32, GPUTexture>} Tracks which GPUTexture is bound per binding slot (managed by CustomShaderPipeline). */
  __gpuBoundTextures = new Map();
  /** @type {Map<GPUIndex32, string>} Tracks serialized sampler descriptors per binding slot (managed by CustomShaderPipeline). */
  __gpuBoundSamplers = new Map();


  // Internal backing field for .needsUpdate below.
  _needsUpdate = true;

  _changeCount = 1;

  /** @type {boolean} Set for changes that requires pipeline recreation. */
  get needsUpdate() {
    return this._needsUpdate;
  }
  /** For THREE compatibility this fires an 'update' event when set to true. */
  set needsUpdate(value) {
    if (value === true) {
      this._changeCount++;
      this.dispatchEvent({ type: 'update' });
    }
    this._needsUpdate = value;
  }

  /**
   * Custom Shader Material properties.
   */

  /**
   * @typedef {Object} ShaderSource
   * @property {string} src The string with the shader source code.
   * @property {string} [entryPoint] You can omit entryPoint if only a single function has @vertex / @fragment set.
   */

  /** @type {ShaderSource} */
  fragmentShader = { src: '', entryPoint: undefined };
  /** @type {ShaderSource} */
  vertexShader = { src: '', entryPoint: undefined };
  /** @type {?UniformGroup} */
  uniformGroup = null;
  /** @type {object} */
  defines = {};
  /** @type {Array<TextureBindingInfo>} Array of texture binding descriptors. */
  textureBindings = [];


  /**
   * @param {Object} [initProperties] - Initial properties to assign to the material
   */
  constructor(initProperties) {
    if (initProperties) {
      const { uniformGroup, ...cloneableProps } = initProperties;
      Object.assign(this, structuredClone(cloneableProps));

      // uniformGroup contains non-serializable WebGPU resources and can't be cloned.
      if (uniformGroup !== undefined) {
        this.uniformGroup = uniformGroup;
      }
    }
  }

  /** Copies rendering-state scalars (depth, blending, polygon-offset) to target. */
  _copyRenderState(target) {
    target.label = this.label;
    target.depthWrite = this.depthWrite;
    target.depthTest = this.depthTest;
    target.depthFunc = this.depthFunc;
    target.side = this.side;
    target.blending = this.blending;
    target.transparent = this.transparent;
    target.polygonOffset = this.polygonOffset;
    target.polygonOffsetFactor = this.polygonOffsetFactor;
    target.polygonOffsetUnits = this.polygonOffsetUnits;
  }

  /**
   * Creates a clone of this material. Base properties are copied; renderer-attached
   * state (`__gpu*`) and `uniformGroup` start fresh on the clone.
   * Textures are shared by reference.
   *
   * @param {ShaderMaterialWebGPU} [target] - Subclasses pass a freshly constructed
   *   instance of their own type here. Must be a new object — this method does not
   *   clear prior state like attached GPU data or listeners.
   * @returns {ShaderMaterialWebGPU}
   */
  clone(target) {
    const cloned = target || new ShaderMaterialWebGPU();

    this._copyRenderState(cloned);
    cloned.defines = { ...this.defines };
    // Shader source objects are shared — they are treated as immutable after construction.
    cloned.vertexShader = this.vertexShader;
    cloned.fragmentShader = this.fragmentShader;
    cloned.textureBindings = this.textureBindings.map((b) => ({ ...b }));

    return cloned;
  }

  //  Optional function to refresh uniform values from material properties.
  refreshUniforms() {
  }

  /**
   * Dispose of this material's resources by invoking attached 'dispose' event handlers.
   * Alias for `material.dispatchEvent({ type: 'dispose' });`.
   */
  dispose = function () {
    this.dispatchEvent({ type: 'dispose' });
  };

  //// Inherit THREE.EventListener ////
  addEventListener = THREE.EventDispatcher.prototype.addEventListener;
  hasEventListener = THREE.EventDispatcher.prototype.hasEventListener;
  removeEventListener = THREE.EventDispatcher.prototype.removeEventListener;
  dispatchEvent = THREE.EventDispatcher.prototype.dispatchEvent;
  //// ////

}